import {
  siShopify,
  siWoocommerce,
  siPrestashop,
  siOdoo,
  siAmazon,
  siMagento,
  siEtsy,
  siEbay,
  siWix,
  siDhl,
  siFedex,
  siUps,
  siDpd,
} from 'simple-icons'

type Brand = {
  name: string
  color: string
  type: 'icon' | 'wordmark'
  path?: string
  wordmark?: string
  bg?: string
  logo?: string
}

const ic = (s: { title: string; hex: string; path: string }) => ({
  name: s.title,
  color: `#${s.hex}`,
  type: 'icon' as const,
  path: s.path,
})

const wm = (name: string, wordmark: string, color: string, bg?: string, logo?: string): Brand => ({
  name,
  wordmark,
  color,
  bg,
  logo,
  type: 'wordmark',
})

export const BRANDS: Record<string, Brand> = {
  // Integrations (simple-icons)
  shopify: ic(siShopify),
  woocommerce: ic(siWoocommerce),
  prestashop: ic(siPrestashop),
  odoo: ic(siOdoo),
  amazon: ic(siAmazon),
  magento: ic(siMagento),
  etsy: ic(siEtsy),
  ebay: ic(siEbay),
  wix: ic(siWix),

  // Carriers (simple-icons)
  dhl: ic(siDhl),
  fedex: ic(siFedex),
  ups: ic(siUps),
  dpd: ic(siDpd),

  // Missing carriers → custom wordmark w/ brand colors
  bpost: wm('bpost', 'bpost', '#ffffff', '#E30613', '/carriers/Bpost.svg'),
  gls: wm('GLS', 'GLS', '#061A45', '#FFD100', '/carriers/Gls.svg'),
  postnl: wm('PostNL', 'PostNL', '#ffffff', '#FF6200', '/carriers/PostNl.svg'),
  mondialrelay: wm('Mondial Relay', 'Mondial Relay', '#ffffff', '#E3001A', '/carriers/mondial%20relay.svg'),
  colisprive: wm('Colis Privé', 'Colis Privé', '#ffffff', '#1D3557', '/carriers/Colis%20Prive.svg'),
  deutschepost: wm('Deutsche Post', 'Deutsche Post', '#000000', '#FFCC00', '/carriers/Deutsche%20Post.svg'),
  dsv: wm('DSV', 'DSV', '#ffffff', '#00263E', '/carriers/DSV.svg'),

  // Missing integrations → wordmark
  bol: wm('Bol.com', 'bol.com', '#ffffff', '#0000A4'),
  cdiscount: wm('Cdiscount', 'Cdiscount', '#ffffff', '#FF6F00'),
  lightspeed: wm('Lightspeed', 'Lightspeed', '#ffffff', '#F43545'),
}

export function BrandGlyph({
  id,
  size = 28,
  className = '',
  monochrome = false,
}: {
  id: string
  size?: number
  className?: string
  monochrome?: boolean
}) {
  const b = BRANDS[id]
  if (!b) return null

  if (b.type === 'icon' && b.path) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={size}
        height={size}
        className={className}
        role="img"
        aria-label={b.name}
      >
        <path d={b.path} fill={monochrome ? 'currentColor' : b.color} />
      </svg>
    )
  }

  return null
}

export function BrandTile({
  id,
  className = '',
}: {
  id: string
  className?: string
}) {
  const b = BRANDS[id]
  if (!b) return null

  if (b.type === 'icon' && b.path) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl ${className}`}
        style={{ background: b.color + '14' }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="44%" height="44%" role="img" aria-label={b.name}>
          <path d={b.path} fill={b.color} />
        </svg>
      </div>
    )
  }

  return (
    <div
      className={`flex items-center justify-center rounded-xl px-2 ${className}`}
      style={{ background: b.bg || '#111', color: b.color }}
    >
      <span className="font-bold tracking-tight text-sm sm:text-base">{b.wordmark}</span>
    </div>
  )
}

export function BrandPill({ id, className = '' }: { id: string; className?: string }) {
  const b = BRANDS[id]
  if (!b) return null

  if (b.type === 'icon' && b.path) {
    return (
      <div className={`inline-flex items-center gap-1.5 ${className}`}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={14} height={14} role="img" aria-label={b.name}>
          <path d={b.path} fill={b.color} />
        </svg>
        <span className="text-xs font-semibold text-ink-800">{b.name}</span>
      </div>
    )
  }

  if (b.logo) {
    return (
      <div className={`inline-flex items-center gap-1.5 ${className}`}>
        <img src={b.logo} alt={b.name} width="60" height="16" className="h-4 w-auto" draggable={false} />
      </div>
    )
  }

  return (
    <div
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 ${className}`}
      style={{ background: b.bg, color: b.color }}
    >
      <span className="text-[9px] font-bold tracking-wide">{b.wordmark}</span>
    </div>
  )
}
